import { createContext, createElement, useContext, useMemo, type ReactNode } from 'react';
import type ViewProductStore from '../../AppData/_stores/ViewProductStore';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import useProductCatalogFiltering from './useProductCatalogFiltering';

export type ProductCatalogProductEntry = [string, ViewProductStore];

const compareNullableOrder = (aOrder: number | null, bOrder: number | null) => {
  if (aOrder === bOrder) return 0;
  if (aOrder === null) return 1;
  if (bOrder === null) return -1;
  return aOrder - bOrder;
}

function useProductCatalogProductsValue() {
  const viewProducts = useAppSelector(state => state.get('appData').get('viewProducts'));
  const {
    matchesProduct,
    selectedCollection,
  } = useProductCatalogFiltering();
  const compareCollectionProducts = selectedCollection?.compareProducts;

  const sortedViewProducts = useMemo(() => {
    const latestNewProductIds = viewProducts
      .filter(product => product.get('is_new'))
      .sortBy(product => -product.get('id'))
      .take(2)
      .map(product => product.get('id'))
      .toSet();

    return viewProducts
      .sort((a, b) => {
        if (compareCollectionProducts !== undefined) {
          return compareCollectionProducts(a, b);
        }

        const aIsLatestNewProduct = latestNewProductIds.has(a.get('id'));
        const bIsLatestNewProduct = latestNewProductIds.has(b.get('id'));
        if (aIsLatestNewProduct !== bIsLatestNewProduct) return aIsLatestNewProduct ? -1 : 1;
        if (aIsLatestNewProduct && bIsLatestNewProduct) return b.get('id') - a.get('id');

        const featuredOrder = compareNullableOrder(a.get('featured_order'), b.get('featured_order'));
        if (featuredOrder !== 0) return featuredOrder;

        const bestsellerOrder = compareNullableOrder(a.get('bestseller_order'), b.get('bestseller_order'));
        if (bestsellerOrder !== 0) return bestsellerOrder;

        if (a.get('is_new') !== b.get('is_new')) {
          return a.get('is_new') ? -1 : 1;
        }

        return b.get('id') - a.get('id');
      })
  }, [compareCollectionProducts, viewProducts])

  const visibleProductEntries = useMemo<ProductCatalogProductEntry[]>(() => {
    return sortedViewProducts.entrySeq().filter(([, product]) => {
      return matchesProduct(product);
    }).toArray();
  }, [matchesProduct, sortedViewProducts]);

  return {
    sortedViewProducts,
    visibleProductCount: visibleProductEntries.length,
    visibleProductEntries,
  };
}

type ProductCatalogProductsContextValue = ReturnType<typeof useProductCatalogProductsValue>;

const ProductCatalogProductsContext = createContext<ProductCatalogProductsContextValue | undefined>(undefined);

export function ProductCatalogProductsProvider(props: {
  children: ReactNode
}) {
  const value = useProductCatalogProductsValue();

  return createElement(ProductCatalogProductsContext.Provider, { value }, props.children);
}

export default function useProductCatalogProducts() {
  const contextValue = useContext(ProductCatalogProductsContext);
  if (contextValue === undefined) {
    throw new Error('useProductCatalogProducts must be used within ProductCatalogProductsProvider');
  }

  return contextValue;
}
